import { useMemo, useRef, useState } from 'react';
import { cls } from '../lib/format';

export interface ComboOption {
  value: string;
  label: string;
  sub?: string;
}

interface Props {
  placeholder: string;
  options: ComboOption[];
  selected: string[];
  onChange: (next: string[]) => void;
  maxShown?: number;
}

export function MultiCombobox({ placeholder, options, selected, onChange, maxShown = 8 }: Props) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const matches = useMemo(() => {
    const query = q.trim().toLowerCase();
    const list = query
      ? options.filter(
          (o) => o.label.toLowerCase().includes(query) || o.value.toLowerCase().includes(query),
        )
      : options;
    return list.slice(0, maxShown);
  }, [q, options, maxShown]);

  const selectedSet = useMemo(() => new Set(selected.map((s) => s.toUpperCase())), [selected]);
  const selectedLabels = useMemo(
    () =>
      selected.map((v) => {
        const o = options.find((x) => x.value.toUpperCase() === v.toUpperCase());
        return { value: v, label: o?.label ?? v };
      }),
    [selected, options],
  );

  const add = (v: string) => {
    const val = v.toUpperCase();
    if (!selectedSet.has(val)) onChange([...selected, val]);
    setQ('');
    setOpen(false);
  };

  const remove = (v: string) => onChange(selected.filter((x) => x !== v));

  return (
    <div ref={wrapRef} className="relative">
      {selectedLabels.length > 0 && (
        <div className="mb-1.5 flex flex-wrap gap-1">
          {selectedLabels.map((s) => (
            <button
              key={s.value}
              onClick={() => remove(s.value)}
              className="rounded-full bg-accent/20 px-2 py-0.5 text-xs text-accent ring-1 ring-accent/40"
            >
              {s.label} ✕
            </button>
          ))}
        </div>
      )}
      <input
        value={q}
        placeholder={placeholder}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            const first = matches.find((m) => !selectedSet.has(m.value.toUpperCase()));
            if (first) add(first.value);
            else if (q.trim()) add(q.trim());
          }
        }}
        className="w-full rounded-md border border-bg-border bg-bg-raised px-2.5 py-1.5 text-sm text-slate-100 outline-none placeholder:text-slate-500 focus:border-accent"
      />
      {open && matches.length > 0 && (
        <div className="scroll-thin absolute z-30 mt-1 max-h-56 w-full overflow-auto rounded-md border border-bg-border bg-bg-panel shadow-xl">
          {matches.map((o) => {
            const taken = selectedSet.has(o.value.toUpperCase());
            return (
              <button
                key={o.value}
                disabled={taken}
                onMouseDown={(e) => {
                  e.preventDefault();
                  add(o.value);
                }}
                className={cls(
                  'flex w-full items-center justify-between px-2.5 py-1.5 text-left text-sm',
                  taken ? 'text-slate-600' : 'text-slate-200 hover:bg-bg-raised',
                )}
              >
                <span className="truncate">
                  {o.label}
                  {o.sub && <span className="ml-1 text-xs text-slate-500">{o.sub}</span>}
                </span>
                <span className="ml-2 font-mono text-xs text-slate-500">{o.value}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
